import { authenticatedFetch } from './auth';

const API_BASE: string | undefined = process.env.REACT_APP_API_BASE;

if (!API_BASE) {
  throw new Error("API base URL is not defined in environment variables");
}

export interface Country {
  id: number;
  name: string;
  isoAlpha2: string;
}

export interface HsLookupResponse {
  id: number;
  hsCode: string;
  description: string;
  shortDescription: string;
  hsCodeFormatted: string;
}

export interface DutyRateResponse {
  id: number;
  hsCode: string;
  originCode: string;
  customsDuty: number;
  measureType: string;
  measureTypeCode?: string;
  legalBase: string;
  startDate: Date;
  endDate?: Date;
  timestamp: Date;
  customsDutyFormatted: string;
  hsCodeFormatted: string;
}

export interface VatRateResponse {
  countryId: number;
  hsCode: string;
  vatRate: number;
}

// --- API functions ---

export const fetchAllCountries = async (): Promise<Country[]> => {
  const response = await fetch(`${API_BASE}/products/all-countries`);
  if (!response.ok) {
    throw new Error(`Error: ${response.status}`);
  }
  return (await response.json()) as Country[];
};

export const fetchNonEuCountries = async (): Promise<Country[]> => {
  const response = await fetch(`${API_BASE}/products/non-eu-countries`);
  if (!response.ok) {
    throw new Error(`Error: ${response.status}`);
  }
  return (await response.json()) as Country[];
};

export const fetchEuCountries = async (): Promise<Country[]> => {
  const response = await fetch(`${API_BASE}/products/eu-countries`);
  if (!response.ok) {
    throw new Error(`Error: ${response.status}`);
  }
  return (await response.json()) as Country[];
};

export const lookupHs = async (
  description: string,
  countryId: number
): Promise<HsLookupResponse[]> => {
  const res = await authenticatedFetch(`${API_BASE}/products/hs-lookup`, {
    method: "POST",
    body: JSON.stringify({ description, countryId }),
  });

  if (!res.ok) throw new Error(`Failed: ${res.status}`);
  return (await res.json()) as HsLookupResponse[];
};

export const fetchDutyRate = async (
  hsCode: string,
  countryId: number
): Promise<DutyRateResponse> => {
  const res = await authenticatedFetch(`${API_BASE}/products/duty-rate`, {
    method: "POST",
    body: JSON.stringify({ hsCode, countryId }),
  });

  if (!res.ok) {
    throw new Error(`Failed to fetch duty rates: ${res.statusText}`);
  }

  return (await res.json()) as DutyRateResponse;
};

export const fetchVatRate = async (
  hsCode: string,
  countryId: number
): Promise<VatRateResponse> => {
  const res = await authenticatedFetch(`${API_BASE}/products/vat-rate`, {
    method: "POST",
    body: JSON.stringify({ hsCode, countryId }),
  });

  if (!res.ok) {
    throw new Error(`Failed to fetch vat rate: ${res.statusText}`);
  }

  return (await res.json()) as VatRateResponse;
};

// --- Auth API ---
export interface VerifyRecaptchaResponse {
  token: string;
}

export const verifyRecaptcha = async (
  recaptchaToken: string
): Promise<VerifyRecaptchaResponse> => {
  const res = await fetch(`${API_BASE}/auth/token?token=${encodeURIComponent(recaptchaToken)}`, {
    method: "GET",
  });

  if (!res.ok) {
    throw new Error(`Failed to verify reCAPTCHA: ${res.statusText}`);
  }

  return (await res.json()) as VerifyRecaptchaResponse;
};
